/**
 * __root.tsx — app-wide providers + legacy redirect handler.
 *
 * Mounts:
 *   - <ErrorBoundary>: outermost catch for uncaught render errors
 *     (Stage 7 — essentials.md §6). Renders a minimal "copy details /
 *     reload" page. Sits OUTSIDE providers so a crash inside Clerk or
 *     QueryClient bootstrap still renders a usable screen.
 *   - QueryClientProvider: a single fresh QueryClient.
 *   - ShellProvider: cross-component sidebar state.
 *   - CommandK: global mod+k keybinding.
 *   - Legacy redirect handler: translates old `apps/dashboard` paths
 *     to the new topology.
 *
 * Also wires:
 *   - `notFoundComponent: NotFoundScreen` — every unmatched route
 *     lands on the 404 page.
 *   - `errorComponent: AppCrashScreen` — router-level render errors
 *     (inside the Outlet tree, so `ErrorBoundary` won't catch them).
 *     Receives TanStack Router's `{ error, reset }` pair.
 */

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppCrashScreen, NotFoundScreen } from '@/components/RouteError';
import { Toaster } from '@/components/Toaster';
import { VersionWatcher } from '@/components/VersionWatcher';
import { CommandK } from '@/components/shell/CommandK';
import { ShellProvider } from '@/components/shell/ShellContext';
import { ClerkRootProvider } from '@/lib/clerk';
import { DEMO_EMBED } from '@/lib/demo';
import { queryClient as moduleQueryClient } from '@/lib/query-client';
import {
  CACHE_BUSTER,
  CACHE_MAX_AGE_MS,
  createQueryPersister,
  shouldDehydrateQuery,
} from '@/lib/query-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';

// -----------------------------------------------------------------
// Legacy redirect map (topology.md + essentials.md §2)
//
// Stage 2 deviation from topology.md: the new app runs without a
// `/app/` URL prefix (dashboard.clickweave.co already implies "app").
// All legacy URLs keep the `/app/*` shape and rewrite to the new
// un-prefixed equivalents:
//
//   legacy                     → new
//   /app/audits                → /settings/logs
//   /app/audits/:id            → /settings/logs/:id
//   /app/keys                  → /settings/keys
//   /app/members               → /settings/members
//   /app/billing               → /settings/billing      (also covers
//                                apps/api's 402 upgrade_url; essentials §2)
//   /app                       → /
//   /app/settings              → /settings (→ /settings/profile via
//                                the settings layout's index redirect)
//   /app/* (catch-all)         → strip the /app prefix
//
// PR 3 of the analytics-shell redesign collapsed `/sites/*` out of
// URLs entirely (the active site is chrome state, not a URL
// segment). With no live users the `/sites/*` legacy redirects we'd
// have needed to preserve those bookmarks were dropped — the
// `/app/sites/:siteUuid` and `/origins → /sites` redirects that
// previously sat here went with them. The remaining `/app/*` rules
// stay because `apps/api`'s 402 response still emits
// `/app/billing` as the upgrade URL.
// -----------------------------------------------------------------

const staticRedirects: Array<{ pattern: RegExp; to: (m: RegExpMatchArray) => string }> = [
  { pattern: /^\/app\/audits\/?$/, to: () => '/settings/logs' },
  { pattern: /^\/app\/audits\/([^/]+)\/?$/, to: (m) => `/settings/logs/${m[1]}` },
  { pattern: /^\/app\/keys\/?$/, to: () => '/settings/keys' },
  { pattern: /^\/app\/members\/?$/, to: () => '/settings/members' },
  { pattern: /^\/app\/billing\/?$/, to: () => '/settings/billing' },
];

function stripAppPrefix(pathname: string): string | null {
  if (pathname === '/app' || pathname === '/app/') return '/';
  if (pathname.startsWith('/app/')) return pathname.slice(4); // drop "/app"
  return null;
}

/**
 * Runs on every navigation and rewrites legacy URLs to the new
 * topology. Rendered once inside the root so Stage 5 routes don't
 * each have to guard against old paths.
 *
 * Note on `/pages/:pageUuid` (and its older `/app/` twin): the page
 * detail route was deprecated 2026-07-26 — its per-page breakdown
 * lives on Audits › Pages now, so bookmarks land there with the page
 * preselected via `?page=`.
 */
function LegacyRedirects(): null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  useEffect(() => {
    // /pages/:pageUuid (+ /app/pages/:pageUuid) → Audits › Pages
    const pageMatch = pathname.match(/^(?:\/app)?\/pages\/([^/]+)\/?$/);
    if (pageMatch) {
      navigate({
        to: '/performance/audits',
        search: { page: pageMatch[1] ?? '' },
        replace: true,
      });
      return;
    }

    // Specific pattern redirects.
    for (const { pattern, to } of staticRedirects) {
      const match = pathname.match(pattern);
      if (match) {
        navigate({ to: to(match), replace: true });
        return;
      }
    }

    // Catch-all: anything still under /app/... drops the prefix.
    const stripped = stripAppPrefix(pathname);
    if (stripped !== null && stripped !== pathname) {
      navigate({ to: stripped, replace: true });
    }
  }, [pathname, navigate]);

  return null;
}

// -----------------------------------------------------------------
// Root component
// -----------------------------------------------------------------

// Module-scoped persister so we instantiate exactly once. `null` only
// when running outside a browser (SSR / tests without jsdom), in which
// case `PersistQueryClientProvider` no-ops and we degrade to in-memory
// caching cleanly. See `lib/query-persister.ts` for the whitelist of
// queries we actually persist (workspace + sites list/detail/pages-
// with-audits + pages detail — everything else stays in-memory).
const persister = createQueryPersister();

function RootShell() {
  // Tuned `QueryClient`: caches per session, with 401/402/error
  // routing in its `QueryCache` + `MutationCache` handlers. Module-
  // scoped (defined in `lib/query-client.ts`, NOT `main.tsx` — that
  // import was circular with the route tree) so route loaders can
  // consume it via the router context — loaders run outside React
  // render and need a stable reference.
  const queryClient = moduleQueryClient;

  // `PersistQueryClientProvider` rehydrates the cache from
  // localStorage synchronously before first paint, so common
  // navigations (audits landing, breadcrumbs) render with cached data
  // immediately on hard reload — no auth-floor + network wait. Falls
  // back to plain `QueryClientProvider` semantics when `persister` is
  // null (non-browser environments).
  return (
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: persister!,
          maxAge: CACHE_MAX_AGE_MS,
          buster: CACHE_BUSTER,
          dehydrateOptions: { shouldDehydrateQuery },
        }}
      >
        <ClerkRootProvider>
          <Toaster>
            <ShellProvider>
              <LegacyRedirects />
              <VersionWatcher />
              {/* No command palette inside marketing embed slices — it can
                  navigate, and the frame is locked to one surface. */}
              {!DEMO_EMBED && <CommandK />}
              <Outlet />
            </ShellProvider>
          </Toaster>
        </ClerkRootProvider>
        {import.meta.env.DEV && (
          <>
            {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> */}
            {/* <TanStackRouterDevtools position="bottom-right" /> */}
          </>
        )}
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );
}

/**
 * Router context shape. Threaded from `main.tsx`'s `createRouter()`
 * call so route loaders can `ensureQueryData` against the same
 * QueryClient that powers `useQuery` hooks in components. Without
 * this typed handle, loaders couldn't access the cache.
 */
export interface RouterContext {
  queryClient: typeof moduleQueryClient;
}

// Embed navigation lock (marketing demo slices, `?embed=1`): the surface
// booted into is the only route the frame may show. Search-param changes
// (tabs / filters / metric toggles) share the pathname and pass through;
// anything that would change route bounces back to the boot URL.
//
// Armed from `loader`, not `beforeLoad`: loaders run only once a
// navigation has SETTLED, so a redirect thrown while booting — a legacy
// `/performance?view=field` link, still baked into www's embeds, landing
// on `/performance/field` — locks the surface it *arrived* at. Arming in
// `beforeLoad` locked the pre-redirect URL instead, and the two then
// fought each other forever: the route redirected to the plane, the lock
// bounced back to `?view=`, the route redirected again. That loop pegged
// the renderer and took the whole marketing page down with it.
let embedBootHref: string | null = null;

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ location }) => {
    if (!DEMO_EMBED || embedBootHref === null) return;
    if (location.pathname !== new URL(embedBootHref, window.location.origin).pathname) {
      throw redirect({ href: embedBootHref });
    }
  },
  loader: ({ location }) => {
    if (DEMO_EMBED && embedBootHref === null) embedBootHref = location.href;
  },
  component: RootShell,
  notFoundComponent: NotFoundScreen,
  errorComponent: ({ error, reset }) => <AppCrashScreen error={error} reset={reset} />,
});
